import { v4 } from "uuid";

/**
 * Creates a GUID with browser crypto, falling back to uuid when crypto throws or is unavailable.
 *
 * Postconditions: Returns the generated ID; fallback-generator failures propagate.
 */
export const createAnonymousUserGuid = () => {
    try {
        return crypto.randomUUID();
    } catch {
        return v4();
    }
};

/**
 * Reuses a truthy stored anonymous ID or creates and stores one.
 *
 * Postconditions: Successful storage persists the ID for later calls. Storage/read failures
 * return a newly generated ID without persistence; the stored value is not format-validated.
 * Assumptions: GUID-generator errors in the fallback path propagate.
 */
export const getSentryAnonymousUserGuid = () => {
    try {
        const storedUserId = localStorage.getItem("sentry-user-id");
        if (storedUserId) {
            return storedUserId;
        }

        const userId = createAnonymousUserGuid();
        localStorage.setItem("sentry-user-id", userId);
        return userId;
    } catch {
        return createAnonymousUserGuid();
    }
};

/**
 * Builds Sentry identity fields from the stored or newly generated anonymous GUID.
 *
 * Postconditions: Returns an identity object without updating Sentry itself. GUID lookup may
 * write local storage; without storage, separate calls may obtain different identities.
 */
export const createSentryAnonymousUser = () => {
    const userGuid = getSentryAnonymousUserGuid();

    return {
        id: `Anonymous - ${userGuid}`,
        username: `Anonymous - ${userGuid}`,
        fullName: "Anonymous User"
    };
};
